export type AppLanguage = "en" | "ja";

export const defaultAppLanguage: AppLanguage = "en";
export const appLanguageStorageKey = "kuii.app-language";

function normalizeLanguage(value: string | null | undefined): AppLanguage | null {
  const normalizedValue = value?.trim().toLowerCase() ?? "";

  if (!normalizedValue) {
    return null;
  }

  if (normalizedValue.startsWith("ja")) {
    return "ja";
  }

  if (normalizedValue.startsWith("en")) {
    return "en";
  }

  return null;
}

export function isAppLanguage(value: string | null | undefined): value is AppLanguage {
  return normalizeLanguage(value) !== null;
}

export function detectBrowserAppLanguage(): AppLanguage {
  if (typeof window === "undefined") {
    return defaultAppLanguage;
  }

  const candidates = [...(window.navigator.languages ?? []), window.navigator.language];

  for (const candidate of candidates) {
    const language = normalizeLanguage(candidate);

    if (language) {
      return language;
    }
  }

  return defaultAppLanguage;
}

export function readStoredAppLanguage(): AppLanguage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return normalizeLanguage(window.localStorage.getItem(appLanguageStorageKey));
  } catch {
    return null;
  }
}

export function writeStoredAppLanguage(language: AppLanguage) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(appLanguageStorageKey, language);
  } catch {
    // Ignore storage failures.
  }
}

export function getInitialAppLanguage(): AppLanguage {
  return readStoredAppLanguage() ?? detectBrowserAppLanguage();
}

export function getIntlLocale(language: AppLanguage) {
  return language === "ja" ? "ja-JP" : "en-US";
}

export function getActiveAppLanguage(): AppLanguage {
  if (typeof document !== "undefined") {
    const documentLanguage = normalizeLanguage(document.documentElement.lang);

    if (documentLanguage) {
      return documentLanguage;
    }
  }

  return readStoredAppLanguage() ?? detectBrowserAppLanguage();
}

export function getActiveIntlLocale() {
  return getIntlLocale(getActiveAppLanguage());
}
